import { CSSTransition } from 'react-transition-group';
import ReactDOM from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './style.module.scss';
import Input from '../Input';

interface ListItem {
  value: string;
  zhName: string;
  enName: string;
  code?: string;
}

interface GroupListSelectorProps {
  value?: ListItem;
  children?(args: { showPanel(): void; value?: ListItem }): React.ReactElement;
  onChange?(value: ListItem): void;
  onVisibleChange?(visible: boolean): void;
  language?: 'zh' | 'en';
  dataSource: Record<string, ListItem[]>;
  title?: string;
  onBeforeOpen?: () => boolean;
  className?: string;
}

const GroupListSelector = ({
  children,
  value,
  onChange,
  onVisibleChange,
  language = 'zh',
  dataSource = {},
  title,
  onBeforeOpen,
  className,
}: GroupListSelectorProps) => {
  const [innerVisible, setVisible] = useState(false);
  const [touchingBar, setTouchingBar] = useState(false);
  const letterContainerRef = useRef<HTMLDivElement>(null);
  const anchorsRef = useRef<Record<string, HTMLDivElement>>({});
  const codeItemRef = useRef<Record<string, HTMLDivElement>>({});
  const [innerValue, setValue] = useState(value);
  const touchTemp = useRef([0, 0]);

  const [letters, setLetters] = useState(Object.keys(dataSource));
  const [searchDataSource, setSearchDataSource] = useState(dataSource);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setValue(value);
  }, [value]);

  useEffect(() => {
    setLetters(Object.keys(dataSource));
    setSearchDataSource(dataSource);
  }, [dataSource]);

  useEffect(() => {
    onVisibleChange?.(innerVisible);
    document.body.style.overflow = innerVisible ? 'hidden' : '';

    // 关闭弹窗时，重置搜索结果和清空输入框
    if (!innerVisible) {
      setLetters(Object.keys(dataSource));
      setSearchDataSource(dataSource);
      setSearchValue('');
    }
  }, [innerVisible, dataSource]);

  useEffect(() => {
    if (innerVisible && innerValue?.value) {
      codeItemRef.current[innerValue.value]?.scrollIntoView({ block: 'center' });
    }
  }, [innerVisible]);

  useEffect(() => {
    const handleTouchEnd = () => {
      setTouchingBar(false);
    };

    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const search = (searchText: string) => {
    if (!searchText) {
      setLetters(Object.keys(dataSource));
      setSearchDataSource(dataSource);
      return;
    }

    const text = searchText.toLocaleLowerCase();
    const searchLetters: string[] = [];
    const searchCountryData: Record<string, ListItem[]> = {};

    Object.keys(dataSource).forEach((letter: string) => {
      dataSource[letter]?.forEach((item: ListItem) => {
        const zhNameMatch = item.zhName.includes(searchText);
        const enNameMatch = item.enName.toLocaleLowerCase().includes(text);

        if (zhNameMatch || enNameMatch) {
          if (!searchLetters.includes(letter)) {
            searchLetters.push(letter);
          }
          if (searchCountryData[letter]) {
            searchCountryData[letter].push(item);
          } else {
            searchCountryData[letter] = [item];
          }
        }
      });
    });

    if (searchLetters.length) {
      setLetters(searchLetters);
      setSearchDataSource(searchCountryData);
    } else {
      setLetters([]);
      setSearchDataSource({});
    }
  };

  const handleStartFindLetter: React.TouchEventHandler<HTMLDivElement> = (e) => {
    setTouchingBar(true);
    const { currentTarget } = e;
    const { top } = currentTarget.getBoundingClientRect();
    const letterDOMs = Array.from(currentTarget.children);
    const itemHeight = letterDOMs.pop()!.clientHeight;
    const paddingTop = parseInt(getComputedStyle(currentTarget).paddingTop, 10);
    touchTemp.current = [top + paddingTop, itemHeight];
  };

  const scrollToAnchorByLetter = async (letter: string) => {
    await anchorsRef.current[letter]?.scrollIntoView();
  };

  const handleTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
    const { touches } = e;
    const [offsetTop, itemHeight] = touchTemp.current;
    const touchOffsetTop = touches[0].clientY - offsetTop;

    if (touchOffsetTop > 0) {
      const index = Math.ceil(touchOffsetTop / itemHeight) - 1;
      scrollToAnchorByLetter(letters[index]);
    }
  };

  const handleClick = () => {
    if (onBeforeOpen && !onBeforeOpen()) {
      return;
    }
    setVisible(true);
  };

  const child = children?.({ showPanel: handleClick, value: innerValue });

  return (
    <>
      {child}
      {globalThis.document
        ? ReactDOM.createPortal(
            <>
              <CSSTransition
                unmountOnExit
                timeout={300}
                in={innerVisible}
                classNames={{
                  enter: styles.fade,
                  enterActive: styles.fadeActive,
                  exit: styles.fadeOut,
                  exitActive: styles.fadeOutActive,
                }}
              >
                <div className={styles.mask} onClick={() => setVisible(false)} />
              </CSSTransition>
              <CSSTransition
                unmountOnExit
                timeout={300}
                in={innerVisible}
                classNames={{
                  enter: styles.slideIn,
                  enterActive: styles.slideInActive,
                  exitActive: styles.slideOutActive,
                }}
              >
                <div className={classNames(styles.panel, className)}>
                  <div className={styles.popupHeader}>
                    {title && <div className={styles.title}>{title}</div>}
                    <svg
                      width="20px"
                      height="20px"
                      viewBox="0 0 20 20"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      className={styles.close}
                      onClick={() => setVisible(false)}
                    >
                      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                        <g transform="translate(-343.000000, -100.000000)">
                          <g transform="translate(343.000000, 100.000000)">
                            <circle fill="#E6E6E6" cx="10" cy="10" r="10" />
                            <g
                              transform="translate(10.000000, 10.000000) rotate(-315.000000) translate(-10.000000, -10.000000) translate(4.000000, 4.000000)"
                              fill="#FFFFFF"
                            >
                              <rect x="5" y="0" width="2" height="12" rx="1" />
                              <rect
                                transform="translate(6.000000, 6.000000) rotate(-270.000000) translate(-6.000000, -6.000000) "
                                x="5"
                                y="0"
                                width="2"
                                height="12"
                                rx="1"
                              />
                            </g>
                          </g>
                        </g>
                      </g>
                    </svg>
                  </div>
                  {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
                  <div
                    className={styles.searchInputContainer}
                    role="button"
                    onClick={(e: any) => {
                      e.stopPropagation();
                    }}
                  >
                    <Input
                      placeholder={language === 'en' ? 'Search' : '搜索'}
                      className={styles.searchInput}
                      value={searchValue}
                      onChange={(e) => {
                        const { value: searchText } = e.currentTarget;
                        setSearchValue(searchText);
                        search(searchText);
                      }}
                    />
                  </div>
                  <div className={classNames(styles.countryCodes, touchingBar && styles.touchingStatic)}>
                    {letters.map((letter, i) => (
                      <div key={i} className={styles.codeGroup}>
                        <div
                          className={styles.letter}
                          tabIndex={-1}
                          ref={(dom) => {
                            if (dom) {
                              anchorsRef.current[letter] = dom;
                            }
                          }}
                        >
                          {letter}
                        </div>
                        {searchDataSource[letter].map((item, i) => (
                          <div
                            className={classNames(styles.codeItem, innerValue?.value === item.value && styles.selected)}
                            key={i}
                            onClick={() => {
                              setValue(item);
                              onChange?.(item);
                              setVisible(false);
                            }}
                            ref={(codeDom) => {
                              if (codeDom) {
                                codeItemRef.current[item.value] = codeDom;
                              }
                            }}
                          >
                            {language === 'en' ? item.enName : item.zhName}
                            {item.code && <div className={styles.code}>{item.code}</div>}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div
                    className={classNames(styles.touchBar, touchingBar && styles.touching)}
                    onTouchStart={handleStartFindLetter}
                    onTouchMove={handleTouchMove}
                    onContextMenu={(e) => {
                      e.preventDefault();
                    }}
                    ref={letterContainerRef}
                  >
                    {letters.map((letter) => (
                      <div
                        key={letter}
                        onClick={() => {
                          setTimeout(() => {
                            scrollToAnchorByLetter(letter);
                          });
                        }}
                        onTouchStart={() => {
                          setTimeout(() => {
                            scrollToAnchorByLetter(letter);
                          });
                        }}
                      >
                        {letter}
                      </div>
                    ))}
                  </div>
                </div>
              </CSSTransition>
            </>,
            document.body,
          )
        : null}
    </>
  );
};

export default GroupListSelector;
